/**
 * Source resolution for inline image previews.
 *
 * A decoded bitmap costs width * height * 4 bytes no matter how small the box
 * it renders into, so pointing a 180px card at a 2048px original pins the
 * whole 16MB RGBA buffer. A thread with many image outputs (Work deliverables)
 * holds every one of those at once, which is what pushed long Work chats into
 * gigabyte territory. Inline previews therefore prefer the ~800px thumbnail
 * generated at upload (see backend/utils/files.js); surfaces that genuinely
 * display at full size, like the Lightbox, opt out.
 */

/** The full-size original, preferring an explicit uri over the file record. */
export function resolveFullSourceUri(uri, file) {
  if (uri) return uri;
  if (!file) return null;
  return file.cdnPath || file.url || file.uri || null;
}

/**
 * @param {Object}  args
 * @param {String}  [args.uri]                  explicit full-size uri
 * @param {Object}  [args.file]                 file record (cdnPath/thumbnailPath/blurhash)
 * @param {Boolean} [args.preferThumbnail]      caller renders into a small box
 * @param {Boolean} [args.isSvg]                vectors stay vector — never downscale
 * @param {Boolean} [args.useThumbnailFallback] the original failed to load
 * @returns {String|null} the uri to load
 */
export function resolvePreviewUri({
  uri,
  file = null,
  preferThumbnail = false,
  isSvg = false,
  useThumbnailFallback = false,
} = {}) {
  const thumbnail = file?.thumbnailPath || null;
  // The original failed (e.g. an SVG the platform can't rasterize): the
  // thumbnail is the only thing left to show.
  if (useThumbnailFallback && thumbnail) return thumbnail;
  if (preferThumbnail && !isSvg && thumbnail) return thumbnail;
  return resolveFullSourceUri(uri, file);
}
